import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { Payment, Transaction } from '@shared/types/payment.types'
import type { BaseFullPageProps } from '@shared/types'
import { TabbedSection } from '../../components/sections/TabbedSection'
import { DetailsList, DetailRow } from '../../components'
import { SectionCard } from '../../components/sections/SectionCard'
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge'
import { RelatedPayments } from './RelatedPayments'
import {
  PAYMENT_DETAILS,
  FEES_DETAILS,
  PAYMENT_SECURITY_DETAILS,
  PAYMENT_METHOD_TAB_DETAILS,
  CUSTOMER_DETAILS,
  MOCK_ACTIVITY_EVENTS,
  SAMPLE_EVENTS,
  RISK_CHECKS,
} from '@shared/data'
import { PaymentActivitySection } from '../../components/domain-sections/Activity'
import { RiskScore, EventsLogsSection, ReceiptsSection } from '../../components'
import { PaymentAdapter } from '@shared/adapters'
import type { PaymentDetail } from '../../types/panel-common.types'

// ── Filtering helpers ────────────────────────────────────────────────────

const isZeroValue = (value: string): boolean => {
  const cleanValue = value.replace(/[A-Z]{3}|\s|€|£|¥|\$|,/g, '').trim()
  const numericValue = parseFloat(cleanValue)
  return numericValue === 0 || cleanValue === '' || cleanValue === '0'
}

const filterFees = (details: PaymentDetail[]): PaymentDetail[] =>
  details.filter(fee => !isZeroValue(fee.value))

const FOLD_AT = 5

// ── FoldableDetails ───────────────────────────────────────────────────────

interface FoldableDetailsProps {
  details: PaymentDetail[]
  isDetailView?: boolean
  specialRenderer?: (detail: PaymentDetail, index: number, isLast: boolean) => React.ReactNode | null
}

const FoldableDetails = React.memo<FoldableDetailsProps>(({
  details,
  isDetailView = false,
  specialRenderer,
}) => {
  const [expanded, setExpanded] = useState(false)

  const hasMore = details.length > FOLD_AT
  const visibleDetails = (isDetailView || expanded) ? details : details.slice(0, FOLD_AT)

  return (
    <div className="flex flex-col gap-1 pt-2">
      <DetailsList
        details={visibleDetails}
        isDetailView={isDetailView}
        specialRenderer={specialRenderer}
      />
      {hasMore && !isDetailView && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="flex items-center gap-1 text-xs text-primary hover:underline mt-2 self-start"
        >
          {expanded ? (
            <>Hide all details <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>View all details <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}
    </div>
  )
})

FoldableDetails.displayName = 'FoldableDetails'

// ── Header Component ──────────────────────────────────────────────────────

interface PaymentHeaderProps {
  payment: Payment
  transactionId: string
}

function PaymentHeader({ payment, transactionId }: PaymentHeaderProps) {
  const getAmount = (): string => {
    if (payment.amount) return String(payment.amount.amount ?? payment.amount)
    const amountDetail = PAYMENT_DETAILS.find(d => d.label === 'Amount')
    return amountDetail?.value || 'N/A'
  }

  const getDate = (): string => {
    if (payment.lastUpdate) return String(payment.lastUpdate)
    const dateDetail = PAYMENT_DETAILS.find(d => d.label === 'Execution Date')
    return dateDetail?.value || 'N/A'
  }

  return (
    <SectionCard className="rounded-2xl">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
            Transaction ID
          </div>
          <div className="text-lg font-semibold">
            {transactionId}
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
            Status
          </div>
          <div>
            <AppStatusBadge entityType="payment" status={payment.status || 'unknown'} size="sm" />
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
            Amount
          </div>
          <div className="text-lg font-semibold">
            {getAmount()}
          </div>
        </div>
        <div className="flex-1">
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
            Date
          </div>
          <div className="text-sm text-muted-foreground">
            {getDate()}
          </div>
        </div>
      </div>
    </SectionCard>
  )
}


// ── Props ──────────────────────────────────────────────────────────────────

interface UnifiedPaymentDetailsProps extends Omit<BaseFullPageProps<Payment>, 'entity'> {
  payment: Payment
  transactionData?: Record<string, unknown>
  onViewPayment?: (payment: Payment) => void
}

// ── Component ──────────────────────────────────────────────────────────────

export const UnifiedPaymentDetails = React.memo<UnifiedPaymentDetailsProps>(({
  payment,
  transactionData,
  onViewPayment,
}) => {
  // Payment section: Details tab with real data or fallback to mock
  const details = useMemo(() => {
    if (transactionData) {
      return new PaymentAdapter().adapt(transactionData as unknown as Transaction)
    }
    return PAYMENT_DETAILS
  }, [transactionData])

  // Payment section: Fees tab
  const filteredFees = useMemo(() => filterFees(FEES_DETAILS), [])

  const feesSpecialRenderer = useMemo(() => {
    return (detail: PaymentDetail, _index: number, _isLast: boolean): React.ReactNode | null => {
      if (detail.label !== 'Net settlement amount (EUR)') return null
      const parts = detail.value.match(/^([\d,.\s]+)\s*\((.*?)\)\s*(\w+)$/)
      const mainValue = parts ? `${parts[3]} ${parts[1].trim()}` : detail.value
      return (
        <DetailRow
          label="Net settlement amount"
          value={mainValue}
          isDetailView={true}
        />
      )
    }
  }, [])

  const paymentTabs = useMemo(() => [
    {
      value: 'details',
      label: 'Details',
      content: (
        <FoldableDetails
          details={details}
          isDetailView={true}
        />
      ),
    },
    {
      value: 'method',
      label: 'Method',
      content: (
        <FoldableDetails
          details={PAYMENT_METHOD_TAB_DETAILS}
          isDetailView={true}
        />
      ),
    },
    {
      value: 'security',
      label: 'Security',
      content: (
        <FoldableDetails
          details={PAYMENT_SECURITY_DETAILS}
          isDetailView={true}
        />
      ),
    },
    {
      value: 'fees',
      label: 'Fees',
      content: (
        <FoldableDetails
          details={filteredFees}
          isDetailView={true}
          specialRenderer={feesSpecialRenderer}
        />
      ),
    },
  ], [details, filteredFees, feesSpecialRenderer])

  return (
    <div className="space-y-4">
      {/* Row 1: Header - Full Width */}
      <PaymentHeader
        payment={payment}
        transactionId={PAYMENT_DETAILS[0]?.value || payment.id || 'N/A'}
      />

      {/* Row 2: Activity | Customer | Receipts - 3 Columns */}
      <div className="grid grid-cols-3 gap-4">
        {/* Activity Section */}
        <SectionCard title="Activity" className="rounded-2xl">
          <PaymentActivitySection
            events={MOCK_ACTIVITY_EVENTS}
          />
        </SectionCard>

        {/* Customer Details Section */}
        <SectionCard title="Customer" className="rounded-2xl">
          <DetailsList
            details={CUSTOMER_DETAILS}
            isDetailView={true}
          />
        </SectionCard>

        {/* Receipts Section */}
        <SectionCard title="Receipts" className="rounded-2xl">
          <ReceiptsSection />
        </SectionCard>
      </div>

      {/* Row 3: Payment Section - Full Width */}
      <SectionCard title="Payment" className="rounded-2xl">
        <TabbedSection
          title=""
          tabs={paymentTabs}
          defaultTab="details"
          contentWidth="w-full"
          hideTitle
        />
      </SectionCard>

      {/* Row 4: Risk Score - Full Width */}
      <SectionCard title="Risk Score" className="rounded-2xl">
        <RiskScore
          riskChecks={RISK_CHECKS}
        />
      </SectionCard>

      {/* Row 5: Related Payments - Full Width */}
      <RelatedPayments
        isDetailView={true}
        onViewPayment={onViewPayment}
        className="rounded-2xl"
      />

      {/* Row 6: Events & Logs - Full Width */}
      <SectionCard title="Events & Logs" className="rounded-2xl">
        <EventsLogsSection
          eventsData={SAMPLE_EVENTS}
        />
      </SectionCard>
    </div>
  )
})

UnifiedPaymentDetails.displayName = 'UnifiedPaymentDetails'
